import { Modal } from '@/components/modal/modal';
import { Alert as CustomAlert } from '@/components/ui/alert'; // Assuming your custom alert path
import { useBrandPaymentMethodsQuery } from '@/hooks/queries/use-brand-payment-methods-query';
import { Button, HStack, RadioCard, Spinner, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddNewPaymentMethodButton } from './AddNewPaymentMethodButton';
import { PaymentMethodOption } from './PaymentMethodOption';
import { StripePaymentForm } from './StripePaymentForm';

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedPaymentMethodId: string) => void;
  currentPaymentMethodId?: string | null;
}

type ModalView = 'list' | 'add';

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPaymentMethodId,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useBrandPaymentMethodsQuery();
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const [view, setView] = useState<ModalView>('list');

  useEffect(() => {
    if (isOpen) {
      // Reset view and selection when modal opens
      setView('list');
      const initialValue =
        currentPaymentMethodId ?? data?.paymentMethods.find((pm) => pm.isDefault)?.id;
      setSelectedValue(initialValue);
    }
  }, [isOpen, currentPaymentMethodId, data]);

  // Animation variants
  const variants = {
    initial: { opacity: 0, y: 20 }, // Slide from bottom
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }, // Slide up on exit
  };

  const handleConfirm = () => {
    if (selectedValue) {
      onConfirm(selectedValue);
      onClose();
    }
  };

  const handleAddNewClick = () => {
    setView('add');
  };

  const handleCancelAdd = () => {
    setView('list');
  };

  const handleAddSuccess = (newPaymentMethodId: string) => {
    // Refetch the list to include the new method
    refetch();
    // Optionally select the newly added method
    setSelectedValue(newPaymentMethodId);
    // Switch back to the list view
    setView('list');
  };

  const renderListView = () => (
    <motion.div
      key="list-view"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }} // Spring transition
    >
      {isLoading && <Spinner />}
      {error && (
        <CustomAlert
          status="error"
          variant="subtle"
          title={t('payouts.checkout.paymentMethodModal.error')}
        >
          {t('payouts.checkout.paymentMethodModal.errorLoading', { message: error.message })}
        </CustomAlert>
      )}
      {data && (
        <RadioCard.Root
          colorPalette="primary"
          variant="outline"
          value={selectedValue}
          onValueChange={(detail) => setSelectedValue(detail.value ?? undefined)}
        >
          <VStack gap={3} align="stretch">
            {data.paymentMethods.map((pm) => (
              <PaymentMethodOption key={pm.id} pm={pm} />
            ))}
            <AddNewPaymentMethodButton onClick={handleAddNewClick} />
          </VStack>
        </RadioCard.Root>
      )}
      <HStack gap={3} justify="flex-end" mt={6}>
        <Button variant="outline" onClick={onClose}>
          {t('payouts.checkout.paymentMethodModal.cancel')}
        </Button>
        <Button colorPalette="gray" onClick={handleConfirm} disabled={!selectedValue || isLoading}>
          {t('payouts.checkout.paymentMethodModal.confirmSelection')}
        </Button>
      </HStack>
    </motion.div>
  );

  const renderAddView = () => (
    <motion.div
      key="add-view"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }} // Spring transition
    >
      <StripePaymentForm onCancel={handleCancelAdd} onSuccess={handleAddSuccess} />
    </motion.div>
  );

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      // Title changes depending on view, or we hide it for the form
      title={
        view === 'list'
          ? t('payouts.checkout.paymentMethodModal.title')
          : t('payouts.checkout.paymentMethodModal.titleAdd')
      }
      maxWidth="500px"
      // Prevent closing via overlay click when in add form?
      // closeOnOverlayClick={view === 'list'}
    >
      <AnimatePresence mode="wait" initial={false}>
        {view === 'list' ? renderListView() : renderAddView()}
      </AnimatePresence>
    </Modal>
  );
};

export default AddPaymentMethodModal;
